import { useState } from "react";
import { Box, Collapsible, Grid, GridItem, Separator, Text } from "@chakra-ui/react-v3";
import { useColorModeValue } from "../../../ui/color-mode";
import { createNoteMatchData } from "../utils/note-processing";
import { HighlightedText } from "./highlighted-text";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

type NoteSnippetsDrawerProps = {
  noteData?: { fullText: string };
  filterQuery: string;
  rowId: string;
  onSnippetClick?: () => void;
};

/**
 * Expandable drawer component that displays note snippets in a grid layout.
 * Shows all matching snippets when a search query is active.
 * Defaults to open state but is collapsible.
 */
export function NoteSnippetsDrawer({
  noteData,
  filterQuery,
  rowId,
  onSnippetClick,
}: NoteSnippetsDrawerProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const leftBorderColor = useColorModeValue("gray.300", "gray.700");
  const labelColor = useColorModeValue("gray.600", "gray.400");

  // If no note data or no search query, don't render
  if (!noteData?.fullText || !filterQuery.trim()) {
    return null;
  }

  // Generate match data using the note processing utility - show all matches
  const matchData = createNoteMatchData(noteData.fullText, filterQuery, 25);

  // If no matches found in the note, don't render
  if (!matchData || matchData.count === 0) {
    return null;
  }

  function handleToggle(e: React.MouseEvent) {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  }

  return (
    <Box
      onClick={e => e.stopPropagation()}
      bg="bg.surface"
      py={3}
      pl={6}
      pr={4}
      borderLeftWidth="3px"
      borderLeftColor={leftBorderColor}
      borderBottomRadius="md"
      borderWidth="1px"
      borderTopWidth="0"
      borderColor="border.subtle"
      boxShadow="sm"
    >
      <Collapsible.Root open={isExpanded}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={isExpanded ? 2 : 0}
        >
          <Text
            fontSize="2xs"
            fontWeight="medium"
            color={labelColor}
            textTransform="uppercase"
            letterSpacing="wide"
          >
            Found in note • {matchData.count} {matchData.count === 1 ? "match" : "matches"}
          </Text>
          <Collapsible.Trigger asChild>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              cursor="pointer"
              onClick={handleToggle}
              _hover={{ opacity: 0.8 }}
              color="fg.muted"
            >
              <Text fontSize="xs">{isExpanded ? "Collapse" : "Expand"}</Text>
              <Box as="span" fontSize="sm">
                {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
              </Box>
            </Box>
          </Collapsible.Trigger>
        </Box>

        <Collapsible.Content>
          <Separator />
          <Box maxH="400px" overflowY="auto" overflowX="hidden" mt={2}>
            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
                xl: "repeat(7, 1fr)",
              }}
              gap={3}
            >
              {matchData.snippets.map((snippet, index) => (
                <GridItem key={`${rowId}-snippet-${index}`}>
                  <Box
                    p={2}
                    bg="bg.subtle"
                    borderColor="border.emphasized"
                    borderWidth="1px"
                    borderRadius="md"
                    fontSize="xs"
                    lineHeight="tall"
                    whiteSpace="normal"
                    wordBreak="break-word"
                    overflow="hidden"
                    cursor="pointer"
                    onClick={e => {
                      e.stopPropagation();
                      onSnippetClick?.();
                    }}
                    _hover={{
                      bg: "bg.muted",
                      transform: "translateY(-1px)",
                      boxShadow: "md",
                    }}
                    transition="all 0.15s ease"
                  >
                    <HighlightedText text={snippet.snippet} searchQuery={filterQuery} />
                  </Box>
                </GridItem>
              ))}
            </Grid>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}
